/**
 * EmptyState Component
 * Componente para estados vacíos con ilustración
 */

import { cn } from "@/lib/utils";
import Button from "./Button";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary";
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  size = "md",
}: EmptyStateProps) {
  const sizes = {
    sm: {
      container: "py-8",
      icon: "w-12 h-12 mb-3",
      title: "text-base",
      description: "text-sm",
    },
    md: {
      container: "py-12",
      icon: "w-16 h-16 mb-4",
      title: "text-lg",
      description: "text-sm",
    },
    lg: {
      container: "py-16",
      icon: "w-20 h-20 mb-5",
      title: "text-xl",
      description: "text-base",
    },
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        sizes[size].container,
        className
      )}
    >
      {icon && (
        <div
          className={cn(
            "flex items-center justify-center text-gray-300",
            sizes[size].icon
          )}
        >
          {icon}
        </div>
      )}
      <h3
        className={cn(
          "font-semibold text-gray-900",
          sizes[size].title
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            "text-gray-500 mt-1.5 max-w-sm",
            sizes[size].description
          )}
        >
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3 mt-5">
          {action && (
            <Button
              variant={action.variant || "primary"}
              onClick={action.onClick}
              size={size === "sm" ? "sm" : "md"}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="ghost"
              onClick={secondaryAction.onClick}
              size={size === "sm" ? "sm" : "md"}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Ilustraciones predefinidas
export function EmptyStateIllustration({ type }: { type: "search" | "empty" | "error" | "success" }) {
  const illustrations = {
    search: (
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="42" cy="42" r="28" stroke="currentColor" strokeWidth="4" className="text-gray-200" />
        <path d="M62 62L78 78" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-gray-300" />
        <circle cx="42" cy="42" r="12" fill="currentColor" className="text-gray-100" />
      </svg>
    ),
    empty: (
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <rect x="20" y="30" width="60" height="50" rx="4" stroke="currentColor" strokeWidth="3" className="text-gray-200" />
        <path d="M20 45H80" stroke="currentColor" strokeWidth="3" className="text-gray-200" />
        <rect x="30" y="55" width="20" height="6" rx="2" fill="currentColor" className="text-gray-200" />
        <rect x="30" y="65" width="30" height="6" rx="2" fill="currentColor" className="text-gray-100" />
      </svg>
    ),
    error: (
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="50" cy="50" r="35" stroke="currentColor" strokeWidth="4" className="text-red-200" />
        <path d="M35 35L65 65M65 35L35 65" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-red-300" />
      </svg>
    ),
    success: (
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="50" cy="50" r="35" stroke="currentColor" strokeWidth="4" className="text-emerald-200" />
        <path d="M35 50L45 60L65 40" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400" />
      </svg>
    ),
  };

  return illustrations[type];
}

export default EmptyState;
